import { Link } from "@tanstack/react-router";
import { forwardRef } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import CommunityScoreWheel from "@/components/community-score-wheel";
import GameCategories from "@/components/game-categories";
import { gameUrl } from "@/helpers/games";
import { cn } from "@/lib/utils";

import type { GameType } from "../../types";
import FeaturedReview from "../components/featured-review";
import { AwardIcon } from "../icons";

interface CardProps {
  className?: string;
  game: GameType;
}

export default forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, game },
  ref
) {
  return (
    <div
      className={cn(
        "relative w-full shrink-0 snap-center overflow-hidden rounded-[6px] bg-yankees-blue",
        className
      )}
      ref={ref}
    >
      <div
        className={cn(
          "relative aspect-[327/190] bg-white",
          'after:absolute after:inset-0 after:inset-shadow-0-0-8 after:inset-shadow-black after:content-[""]'
        )}
      >
        <CloudinaryImage className="size-full" game={game} rW={654} />
        {!!game.awards.length && (
          <AwardIcon className="absolute top-0 left-[15px] h-auto w-1/10 max-w-10" />
        )}
      </div>
      <div className="flex flex-col items-stretch gap-2.5 px-4 py-3.5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-14/[1.2em] text-white">
              <Link className="link-overlay" to={gameUrl(game)}>
                {game.name}
              </Link>
            </h2>
            <h3 className="mt-[3px] font-normal text-12/[1.2em] text-white/80">
              {game.location.company.name}
            </h3>
          </div>
          {!!game.communityScorePercentages && (
            <CommunityScoreWheel
              className="size-[21px]"
              scores={game.communityScorePercentages}
            />
          )}
        </div>
        <GameCategories
          categories={[game.primaryCategory, ...game.secondaryCategories]}
          className="gap-2"
        />
        {!!game.featuredReview && (
          <FeaturedReview review={game.featuredReview} />
        )}
      </div>
    </div>
  );
});
